import { memo, useMemo } from 'react';

import { Globe } from 'lucide-react';

import Chip from '@mui/material/Chip';

import { DomainsTableActionsColumn } from './domainsTableColumns/DomainsTableActionsColumn';

import css from './DomainsTable.module.css';

import { Card } from 'v2/components/card/Card';
import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { DomainBotsPanel } from 'v2/pages/domain/telegramAuthBots/domainBotsPanel/DomainBotsPanel';
import { IPagination, Pageable, TableColumn } from 'v2/types';
import { DomainModel } from 'v2/types/DomainModel';
import { TelegramAuthBotModel } from 'v2/types/TelegramAuthBotModel';

interface DomainsTableProps {
  data?: Pageable<DomainModel>;
  pagination: IPagination;
  onRevalidate: VoidFunction;
  bots: TelegramAuthBotModel[];
  onRevalidateBots: VoidFunction;
}

export const DomainsTable = memo<DomainsTableProps>(({ data, pagination, onRevalidate, bots, onRevalidateBots }) => {
  const botsByDomain = useMemo(
    () =>
      bots.reduce<Map<string, TelegramAuthBotModel[]>>((acc, bot) => {
        const list = acc.get(bot.domainId) ?? [];
        acc.set(bot.domainId, [...list, bot]);
        return acc;
      }, new Map()),
    [bots]
  );

  const columns: TableColumn<DomainModel>[] = useMemo(
    () => [
      { id: 'domainName', key: 'domainName', label: 'Domain', ellipsis: true },
      {
        key: 'bots',
        label: 'Bots',
        render: (row) => {
          const count = botsByDomain.get(row.id)?.length ?? 0;
          return count > 0 ? (
            <span>
              {count} {count === 1 ? 'bot' : 'bots'}
            </span>
          ) : (
            <span className={css.noBotsLabel}>No bots</span>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        align: 'center',
        render: (row) => (
          <Chip
            className="font-medium"
            color={row.isActive ? 'success' : 'default'}
            label={row.isActive ? 'Active' : 'Inactive'}
            size="small"
          />
        ),
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <DomainsTableActionsColumn domain={row} onRevalidate={onRevalidate} />,
      },
    ],
    [botsByDomain, onRevalidate]
  );

  return (
    <Card className={css.domainTableContainer}>
      <CardHeader title="Available Domains" icon={Globe} />

      <DataTable
        className={css.domainTable}
        columns={columns}
        data={data?.result}
        page={pagination.page}
        total={data?.total}
        onPageChange={pagination.onPageChange}
        pageSize={pagination.pageSize}
        withPagination
        renderExpandedRow={(row) => (
          <DomainBotsPanel domainId={row.id} bots={botsByDomain.get(row.id) ?? []} onRevalidate={onRevalidateBots} />
        )}
      />
    </Card>
  );
});

DomainsTable.displayName = 'DomainsTable';
